// src/components/Header.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useGlobalStateContext } from "../../context/globalContext";
import { useAppDispatch } from "../../hooks";
import { getVacancies } from "../../store/actions";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import UnitivaWhiteLogo from "../../assets/solo-scritta-bianca.svg";
import UnitivaColoredLogo from "../../assets/solo-scritta-colored.svg";
import ArrowRight from "../../assets/ArrowRight.svg";
import { menuData } from "./menuData";
import Container from "../Container";
import {
  HoverMenu,
  HoverMenuColumn,
  MobileMegaMenu,
  MobileMegaMenuOverlay,
} from "./style";
import * as S from "./style";

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const { context } = useGlobalStateContext();
  const location = useLocation();
  const { items_total } = useSelector((state: RootState) => state.vacancies);

  const [sticky, setSticky] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [isDesktopMenuVisible, setIsDesktopMenuVisible] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState("");

  const overlay = useRef<HTMLDivElement>(null);
  const megamenuOverlay = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // determine mobile view
  useEffect(() => {
    setIsMobile(window.innerWidth <= 850);
  }, []);

  // fetch vacancies
  useEffect(() => {
    dispatch(getVacancies());
  }, [dispatch]);

  // slide in / out the mobile mega-menu panel
  const closeMegamenuOverlay = () => {
    if (megamenuOverlay.current) {
      megamenuOverlay.current.style.transform = "translateX(-1000vh)";
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsDesktopMenuVisible(false);
        closeMegamenuOverlay();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isMobileMenuVisible) {
      closeMegamenuOverlay();
    }
  }, [isMobileMenuVisible]);

  const toggleMenu = () => {
    if (isMobile) {
      setIsMobileMenuVisible((prev) => {
        const next = !prev;
        if (overlay.current) {
          overlay.current.style.transform = next
            ? "translateY(0)"
            : "translateY(-150vh)";
        }
        document.documentElement.style.overflowY = next ? "hidden" : "initial";
        return next;
      });
    } else {
      setIsDesktopMenuVisible((prev) => !prev);
    }
  };

  const onLinkClick = (_e: any, selected: string) => {
    setSelectedSolution(selected);
    if (megamenuOverlay.current) {
      megamenuOverlay.current.style.transform = "translateX(0)";
    }
  };

  // responsive handler
  useEffect(() => {
    const handleResize = () => {
      const nowMobile = window.innerWidth <= 850;
      setIsMobile(nowMobile);
      if (overlay.current) {
        if (!nowMobile) {
          setIsMobileMenuVisible(false);
          overlay.current.style.transform = "translateY(-150vh)";
          document.documentElement.style.overflowY = "initial";
        } else if (isMobileMenuVisible) {
          overlay.current.style.transform = "translateY(0)";
          document.documentElement.style.overflowY = "hidden";
        }
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuVisible]);

  // sticky nav on scroll
  const handleScroll = useCallback(() => {
    setSticky(window.scrollY >= 20);
  }, []);
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // don't render header on home
  if (location.pathname === "/") return null;

  return (
    <>
      {/* mobile/fullscreen overlay nav */}
      <S.Overlay
        ref={overlay}
        className={`${context.isCaseStudy ? "" : "menu__dark"} nav__overlay`}
      >
        <Container>
          <nav className="nav__overlay__links">
            <ul>
              <li className={location.pathname === "/" ? "active" : ""}>
                <a href="/">Home</a>
              </li>
              <li className={location.pathname === "/about" ? "active" : ""}>
                <a href="/about">About</a>
              </li>
              <li
                className={location.pathname === "/case-study" ? "active" : ""}
              >
                <a href="/case-study">Case Study</a>
              </li>
              <MobileMegaMenu>
                <h3>Solutions</h3>
                {menuData.columns.map((column, i) => (
                  <button
                    key={i}
                    onClick={(e) => onLinkClick(e, column.header.title)}
                  >
                    {column.header.title}
                    <span>
                      <img src={ArrowRight} alt="arrow" />
                    </span>
                  </button>
                ))}
              </MobileMegaMenu>
              <li className={location.pathname === "/magazine" ? "active" : ""}>
                <a href="/magazine">Magazine</a>
              </li>
              <li className={location.pathname === "/contact" ? "active" : ""}>
                <a href="/contact">Contatti</a>
              </li>
              <li className={location.pathname === "/careers" ? "active" : ""}>
                <a href="/careers">Career</a>
              </li>
            </ul>
          </nav>
        </Container>
      </S.Overlay>

      <MobileMegaMenuOverlay
        ref={megamenuOverlay}
        className={`${context.isCaseStudy ? "" : "menu__dark"}`}
      >
        <div className="container">
          <button onClick={closeMegamenuOverlay}>
            <span>
              <img src={ArrowRight} alt="arrow" />
            </span>
            <span>indietro</span>
          </button>
          {menuData.columns
            .filter((col) => col.header.title === selectedSolution)
            .map((col, idx) => (
              <ul key={idx}>
                <li>
                  <h3>
                    <a href={col.header.url}>{col.header.title}</a>
                  </h3>
                </li>
                {col.items.map((item, j) => (
                  <li key={j}>
                    <a href={item.url}>{item.title}</a>
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </MobileMegaMenuOverlay>

      {/* main nav */}
      <S.Header>
        <Container>
          <S.Navigation
            sticky={sticky}
            isVisible={isMobile ? isMobileMenuVisible : isDesktopMenuVisible}
            className={`${
              context.isCaseStudy
                ? "sticky__white__nav nav__overlay__links"
                : "sticky__dark__nav nav__overlay__links"
            } ${!isMobile && isDesktopMenuVisible ? "menu__open" : ""}`}
          >
            <div className="nav__brand">
              <a href="/">
                <img
                  className="unitiva-logo"
                  src={
                    context.isCaseStudy ? UnitivaColoredLogo : UnitivaWhiteLogo
                  }
                  alt="Unitiva"
                />
              </a>
            </div>

            <div className="nav__links">
              <ul>
                <li>
                  <a href="/">Home</a>
                </li>
                <li>
                  <a href="/about">About</a>
                </li>
                <li>
                  <Link
                    to="/case-study"
                    className={`case-study ${
                      location.pathname.includes("/case-study") ? "active" : ""
                    }`}
                  >
                    Case Study
                  </Link>
                </li>
                <li>
                  <button
                    ref={buttonRef}
                    onClick={toggleMenu}
                    className={`solution-button ${
                      (isMobile ? isMobileMenuVisible : isDesktopMenuVisible)
                        ? "active"
                        : ""
                    }`}
                  >
                    Solutions
                  </button>
                </li>
                <li>
                  <Link
                    to="/magazine"
                    className={`magazine ${
                      location.pathname.includes("/magazine") ? "active" : ""
                    }`}
                  >
                    Magazine
                  </Link>
                </li>
                <li>
                  <a href="/contact">Contatti</a>
                </li>
                <li>
                  <Link to="/careers" className="career">
                    Career
                    <span>
                      {items_total !== 0 && items_total}
                      {items_total === 1
                        ? " posizione aperta"
                        : " posizioni aperte"}
                    </span>
                  </Link>
                </li>
              </ul>
            </div>

            <div
              className={`nav__menu ${
                (isMobile ? isMobileMenuVisible : isDesktopMenuVisible)
                  ? "is__open"
                  : ""
              }`}
              onClick={toggleMenu}
            >
              <svg height="60" width="60">
                <circle cx="30" cy="30" r="28" stroke="black" />
              </svg>
              <div className="nav__menu__icon">
                <span />
                <span />
                <span />
              </div>
            </div>
          </S.Navigation>
        </Container>
      </S.Header>

      {/* desktop hover-menu */}
      {!isMobile && (
        <HoverMenu
          ref={menuRef}
          isVisible={isDesktopMenuVisible}
          className={context.isCaseStudy ? "" : "menu__dark"}
        >
          {menuData.columns.map((column, idx) => (
            <HoverMenuColumn
              key={idx}
              className={
                column.header.title === "Industries" ? "column__divider" : ""
              }
            >
              <ul>
                <li>
                  <h3 className="header">
                    {column.header.url ? (
                      <a href={column.header.url}>{column.header.title}</a>
                    ) : (
                      column.header.title
                    )}
                  </h3>
                </li>
                {column.items.map((item, j) => (
                  <li key={j}>
                    <a href={item.url}>{item.title}</a>
                  </li>
                ))}
              </ul>
            </HoverMenuColumn>
          ))}
        </HoverMenu>
      )}
    </>
  );
};

export default Header;
